import { isoWeekday } from './positions';

/** Un mes del calendario del formulario de horarios. */
export interface MesDelCalendario {
  anio: number;
  /** 1 = enero … 12 = diciembre. */
  mes: number;
  titulo: string;
  /** Semanas de lunes a domingo, siempre de 7 casillas; `null` es relleno fuera del mes. */
  semanas: (string | null)[][];
}

// es-PE: «setiembre», no «septiembre».
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto',
  'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const LETRA_DIA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

const dos = (n: number): string => String(n).padStart(2, '0');

function diasDelMes(anio: number, mes: number): number {
  return new Date(Date.UTC(anio, mes, 0, 12)).getUTCDate();
}

/** Un mes que se está mirando en el calendario. */
export interface MesVisto {
  anio: number;
  /** 1 = enero … 12 = diciembre. */
  mes: number;
}

/** El mes entero, en semanas de lunes a domingo; la pantalla deshabilita lo que cae fuera. */
export function mesDelCalendario(anio: number, mes: number): MesDelCalendario {
  const semanas: (string | null)[][] = [];
  let semana: (string | null)[] = Array<null>(isoWeekday(`${anio}-${dos(mes)}-01`) - 1).fill(null);
  for (let d = 1; d <= diasDelMes(anio, mes); d++) {
    semana.push(`${anio}-${dos(mes)}-${dos(d)}`);
    if (semana.length === 7) {
      semanas.push(semana);
      semana = [];
    }
  }
  if (semana.length > 0) semanas.push([...semana, ...Array<null>(7 - semana.length).fill(null)]);
  return { anio, mes, titulo: `${MESES[mes - 1]} ${anio}`, semanas };
}

export function moverMes(m: MesVisto, delta: number): MesVisto {
  const indice = m.anio * 12 + (m.mes - 1) + delta;
  return { anio: Math.floor(indice / 12), mes: (indice % 12) + 1 };
}

/**
 * Dónde abre el calendario: en el mes de la primera fecha suelta elegida DENTRO de la campaña, para
 * que al editar un horario se vean sus fechas; si no hay ninguna, en el de inicio. Una fecha que
 * quedó fuera (la campaña se acortó después) se salta: abriría en un mes todo deshabilitado donde
 * ni siquiera se ve marcada.
 */
export function mesInicial(desde: string, hasta: string, seleccionadas: string[]): MesVisto {
  const primera = [...seleccionadas].sort().find((f) => f >= desde && f <= hasta) ?? desde;
  const [anio, mes] = primera.split('-').map(Number);
  return { anio, mes };
}

/** Si el mes que se mira queda entero antes o después de la campaña, o la toca. */
export function ubicacionDelMes(m: MesVisto, desde: string, hasta: string): 'antes' | 'dentro' | 'despues' {
  const primero = `${m.anio}-${dos(m.mes)}-01`;
  const ultimo = `${m.anio}-${dos(m.mes)}-${dos(diasDelMes(m.anio, m.mes))}`;
  if (ultimo < desde) return 'antes';
  if (primero > hasta) return 'despues';
  return 'dentro';
}

/** Las fechas fuera de [desde, hasta]. Vacío = todas sirven. */
export function fechasFueraDeRango(fechas: string[], desde: string, hasta: string): string[] {
  return fechas.filter((f) => f < desde || f > hasta);
}

/** `2026-09-05` → `05/09`. */
export function ddmm(fecha: string): string {
  const [, m, d] = fecha.split('-');
  return `${d}/${m}`;
}

/** `2026-12-25` → `25/12/2026`. Para avisos: una campaña puede cruzar de año. */
export function ddmmaaaa(fecha: string): string {
  return `${ddmm(fecha)}/${fecha.slice(0, 4)}`;
}

/** Cómo se lee un horario en la tabla: «S D», «S D + 16/09», «Solo 12/09, 19/09». */
export function resumenDiasDelHorario(weekdays: number[], extraDates: string[], max = 4): string {
  const dias = weekdays.map((d) => LETRA_DIA[d - 1]).join(' ');
  if (extraDates.length === 0) return dias;
  const orden = [...extraDates].sort();
  const lista = orden.slice(0, max).map(ddmm).join(', ')
    + (orden.length > max ? ` y ${orden.length - max} más` : '');
  return weekdays.length === 0 ? `Solo ${lista}` : `${dias} + ${lista}`;
}
